import { GetStaticProps, GetStaticPropsContext } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useCompanies } from "../../common/api/useCompanies";
import { ProgressSpinner } from "../../common/components/ProgressSpinner";
import { COMPANY_ROUTES, LOCATION_ROUTES } from "../../common/constants";
import { useRedirectStore } from "../../common/store/useRedirectStore";

const RedirectPage = () => {
    const router = useRouter();

    const { companies } = useCompanies();

    const { lastCompanyId, lastLocationId } = useRedirectStore();

    useEffect(() => {
        if (!router.isReady || !companies?.length) return;

        // Validate that the user has access to the cached company -> Select cached company or default to the first company in the list
        const cachedCompany = companies.find((c) => c.id === lastCompanyId);
        const company = cachedCompany || companies[0];

        // Edge case: Don't redirect if no company has been found (user should always have at least 1 company)
        if (!company) return;

        // Find last selected location within the cached company or default to the first location if available
        const location = cachedCompany
            ? company.locations?.find((l) => l.id === lastLocationId)
            : company.locations?.[0];

        router.push(
            location
                ? {
                      pathname: LOCATION_ROUTES.DASHBOARD,
                      query: { companyId: company.id, locationId: location.id },
                  }
                : {
                      pathname: COMPANY_ROUTES.LOCATIONS,
                      query: { companyId: company.id },
                  }
        );
    }, [companies, lastCompanyId, lastLocationId, router]);

    return (
        <div className="relative size-full">
            <ProgressSpinner className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 size-40" />
        </div>
    );
};

export const getStaticProps: GetStaticProps = async (
    ctx: GetStaticPropsContext
) => {
    return {
        props: {
            ...(await serverSideTranslations(ctx.locale as string, [
                "common",
                "spaces",
                "setUpSpaces",
                "profile",
                "language",
                "toast",
            ])),
        },
    };
};

export default RedirectPage;
